import { ReactNode, useEffect, useMemo, useState } from 'react';

import ErrorComponent from '@/components/common/error-component/ErrorComponent';
import { Choice } from '@/forms/dto';
import { useApi } from '@/hooks/useApi/useApi';
import useEffectOnlyOnUpdate from '@/hooks/useEffectOnlyOnUpdate/useEffectOnlyOnUpdate';
import { ApiDataType } from '@/types';

import SelectInput from './SelectInput';
import { TSelectInputTheme } from './SelectIntputStyles';
import classes from './SelectWrapper.module.scss';

type Props = {
	name: string;
	label?: string;
	helpText?: string;
	value?: unknown;
	defaultValue?: OptionType;
	disabled?: boolean | undefined;
	readOnly?: boolean | undefined;
	onChange?: (value: unknown, name?: string) => void;
	inputArgs?: object | undefined;
	choices?: Choice[];
	choices_url?: string;
	placeholder?: string | undefined;
	error?: string;
	required?: boolean;
	onClick?: (value: unknown) => void;
	autoFocus?: boolean;
	resDataAfterSubmit?: ApiDataType | null;
	form?: string;
	searchable?: boolean;
	multiple?: boolean;
	optionStyle?: React.CSSProperties;
	reset?: boolean;
	resetCallback?: (value: boolean) => void;
	isClearable?: boolean;
	widgetArgs?: Record<string, boolean>;
	className?: string;
	theme?: TSelectInputTheme;
	menuPortalTarget?: HTMLElement | null | undefined | 'unset';
	startAdornment?: ReactNode;
	endAdornment?: ReactNode;
};

export interface OptionType {
	isdisabled?: boolean;
	name?: string;
	label: string;
	value: string | number | Date;
}

const SelectWrapper = ({
	label,
	name,
	placeholder,
	value,
	defaultValue,
	choices,
	choices_url,
	multiple,
	disabled,
	onChange,
	required,
	autoFocus,
	resDataAfterSubmit,
	form,
	searchable,
	optionStyle,
	reset,
	resetCallback,
	isClearable,
	widgetArgs,
	className,
	theme = 'shadow',
	menuPortalTarget,
	startAdornment,
	endAdornment,
}: Props) => {
	const elId = `id_${name}`;

	const [selectedOption, setSelectedOption] = useState<
		OptionType | OptionType[] | null
	>(null);
	const [error, setError] = useState<string[] | null>(null);
	const [options, setOptions] = useState<OptionType[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const [controlledValue, setControlledValue] = useState<
		OptionType | OptionType[] | undefined
	>();

	const [defaultUncontrolledValue, setDefaultUncontrolledValue] = useState<
		OptionType | OptionType[] | undefined
	>();

	const { apiGet } = useApi();

	const isControlled = !form;
	// Загрузка опций с сервера
	const getChoices = async (
		choicesUrl: string,
		initialChoices?: OptionType[]
	) => {
		try {
			setIsLoading(true);
			const res = await apiGet(`${import.meta.env.VITE_API_URL}${choicesUrl}`);
			let newOptions = [...(initialChoices || [])];

			if (res.data) {
				const data = Array.isArray(res.data)
					? res.data
					: Array.isArray(res.data.data)
						? res.data.data
						: [];

				// Преобразуем данные с сервера в формат OptionType
				// eslint-disable-next-line unicorn/no-array-for-each
				data.forEach((item: OptionType & { id: number; title: string }) => {
					newOptions.push({
						name: item.name || item.title || '',
						label: item.name || item.title || '',
						value: (item.value || item.id).toString(),
					});
				});

				// Удаляем дубликаты
				newOptions = newOptions.filter(
					(opt, index, self) =>
						index ===
						self.findIndex((o) => o.value.toString() === opt.value.toString())
				);

				setOptions(newOptions);
				setError(null);
			}
		} catch {
			setError(['Ошибка загрузки данных']);
		} finally {
			setIsLoading(false);
		}
	};

	// Загрузка опций при монтировании или изменении URL
	useEffect(() => {
		const loadOptions = async () => {
			setIsLoading(true);

			if (choices && choices_url) {
				// Комбинируем статические опции и загруженные с сервера
				const newChoices: OptionType[] = choices.map((item) => ({
					name: item.name,
					label: item.name || '',
					value: (item.value || item.id || '').toString(),
					isdisabled: item.isdisabled,
				}));
				await getChoices(choices_url, newChoices);
			} else if (choices) {
				// Только статические опции
				const newOptions = choices.map((item) => {
					return item.employee
						? {
								name: `${item.employee.first_name} ${item.employee.last_name}`,
								label: `${item.employee.first_name} ${item.employee.last_name}`,
								value: item.employee.id.toString(),
								isdisabled: item.isdisabled,
							}
						: {
								name: item.name,
								label: item.name || '',
								value: (item.value || item.id || '').toString(),
								isdisabled: item.isdisabled,
							};
				});
				setOptions(newOptions);
				setIsLoading(false);
			} else if (choices_url) {
				// Только загруженные с сервера
				await getChoices(choices_url);
			} else {
				setIsLoading(false);
			}
		};

		loadOptions();
	}, [choices_url]);

	// Обновление controlled значения при выборе пользователя
	useEffect(() => {
		if (isControlled && selectedOption) {
			setControlledValue(selectedOption);
		}
	}, [selectedOption, isControlled]);

	// Вызов onChange при изменении выбранной опции
	useEffect(() => {
		if (selectedOption && onChange) {
			if (Array.isArray(selectedOption)) {
				const values = selectedOption.map((i) => i.value.toString());
				onChange(values, name);
			} else {
				onChange(selectedOption.value, selectedOption.name || name);
				if (error && selectedOption.value !== value) {
					setError(null);
				}
			}
		}
	}, [selectedOption]);

	// Обработка ошибок после отправки формы
	useEffectOnlyOnUpdate(() => {
		if (resDataAfterSubmit?.errorMessage?.[name]) {
			setError(resDataAfterSubmit.errorMessage[name]);
		}
	}, [resDataAfterSubmit]);

	// Инициализация значения из пропа value при загрузке опций
	useEffect(() => {
		if (
			options.length > 0 &&
			value !== undefined &&
			value !== null &&
			value !== ''
		) {
			let defaultObject: OptionType | OptionType[] | undefined;

			// eslint-disable-next-line unicorn/prefer-ternary
			if (Array.isArray(value)) {
				// Поиск нескольких опций для множественного выбора
				defaultObject = value
					.map((v) =>
						options.find((item) => item.value.toString() === v.toString())
					)
					.filter(Boolean) as OptionType[];
			} else {
				// Поиск одной опции для одиночного выбора
				defaultObject = options.find(
					(item) => item.value.toString() === value.toString()
				);
			}

			// Устанавливаем значение в зависимости от режима
			if (isControlled) {
				setControlledValue(defaultObject);
			} else {
				setDefaultUncontrolledValue(defaultObject);
			}
		}
	}, [options, value, isControlled]);

	// Сброс значения при reset = true
	useEffect(() => {
		if (reset) {
			setSelectedOption(null);
			setControlledValue(undefined);
			setDefaultUncontrolledValue(undefined);
			resetCallback?.(false);
		}
	}, [reset]);

	const labelAndValue = form === 'projectEditForm';

	// Генерация ключа для принудительного обновления SelectInput
	const selectKey = useMemo(() => {
		if (!isControlled && value && defaultUncontrolledValue) {
			// Для uncontrolled режима - ключ зависит от выбранного значения
			return Array.isArray(defaultUncontrolledValue)
				? defaultUncontrolledValue.map((i) => i.label).join(',')
				: defaultUncontrolledValue.label;
		}
		// Для controlled режима - ключ зависит от имени и количества опций
		return `${name}-${options.length}`;
	}, [isControlled, value, defaultUncontrolledValue, name, options.length]);

	// Пропсы для controlled/uncontrolled режимов
	const controlledProps = {
		value: controlledValue, // controlled режим: управляемое значение
		defaultValue: undefined,
	};

	const uncontrolledProps = {
		value: undefined, // uncontrolled режим: значение по умолчанию
		defaultValue: defaultUncontrolledValue || defaultValue,
	};

	const selectProps = isControlled ? controlledProps : uncontrolledProps;

	return (
		<section
			className={
				name === 'workType'
					? classes['container--workType']
					: [classes.container, className, classes['container-' + theme]]
							.filter(Boolean)
							.join(' ')
			}
			data-error={error ? true : undefined}
		>
			{labelAndValue ? (
				<label>
					<span>{label}</span>
					<span>
						{Array.isArray(defaultUncontrolledValue)
							? defaultUncontrolledValue
									.map((i) => i.name || i.label)
									.join(', ')
							: defaultUncontrolledValue?.name ||
								defaultUncontrolledValue?.label ||
								''}
					</span>
				</label>
			) : (
				label && <label htmlFor={elId}>{label}</label>
			)}

			<SelectInput
				key={selectKey}
				disabled={disabled || isLoading}
				autoFocus={autoFocus && !isLoading}
				onChange={(data) => setSelectedOption(data)}
				options={options}
				id={elId}
				multiple={multiple}
				placeholder={placeholder}
				name={name}
				required={required}
				form={form}
				error={error ? error[0] : undefined}
				searchable={searchable || widgetArgs?.searchable}
				isClearable={isClearable || widgetArgs?.isClearable}
				optionStyle={optionStyle}
				theme={theme}
				menuPortalTarget={menuPortalTarget}
				startAdornment={startAdornment}
				endAdornment={endAdornment}
				{...selectProps}
			/>

			{error?.map((i, idx) => (
				<ErrorComponent key={idx} clazz={classes.error}>
					{`*${i}`}
				</ErrorComponent>
			))}
		</section>
	);
};

export default SelectWrapper;
